import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import apiService, { type AdminChannelRow } from "../services/api";
import { getPodcastImageUrl } from "../utils/imageUtils";
import ChannelDetailDrawer from "./ChannelDetailDrawer";

const CATEGORY_OPTIONS = [
  { value: "politics", label: "Politics" },
  { value: "other", label: "Other" },
] as const;

const TYPE_OPTIONS = [
  { value: "tv", label: "TV" },
  { value: "noticias", label: "Noticias" },
  { value: "radio", label: "Radio" },
  { value: "digital", label: "Digital" },
] as const;

type RowSaveState = "idle" | "saving" | "saved" | "error";

const ChannelAnalysisAdmin: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [rowStates, setRowStates] = useState<Record<string, RowSaveState>>({});

  const selectedChannelId = searchParams.get("channelId");

  const openChannelDrawer = useCallback(
    (row: AdminChannelRow) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("channelId", row._id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeChannelDrawer = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("channelId");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const stopRowClick: React.MouseEventHandler = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, categoryFilter, typeFilter]);

  const queryKey = useMemo(
    () =>
      [
        "admin-channels",
        page,
        rowsPerPage,
        debouncedSearch,
        categoryFilter,
        typeFilter,
      ] as const,
    [page, rowsPerPage, debouncedSearch, categoryFilter, typeFilter],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      apiService.getAdminChannels({
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        category: categoryFilter,
        type: typeFilter,
      }),
    staleTime: 30_000,
  });

  const patchMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: { category?: AdminChannelRow["category"]; type?: AdminChannelRow["type"] };
    }) => apiService.patchAdminChannelTaxonomy(id, payload),
    onMutate: ({ id }) => {
      setRowStates((prev) => ({ ...prev, [id]: "saving" }));
    },
    onSuccess: (result, { id }) => {
      queryClient.setQueryData(queryKey, (old: typeof data | undefined) => {
        if (!old) return old;
        return {
          ...old,
          channels: old.channels.map((ch) =>
            ch._id === id ? result.channel : ch,
          ),
        };
      });
      setRowStates((prev) => ({ ...prev, [id]: "saved" }));
      setTimeout(() => {
        setRowStates((prev) =>
          prev[id] === "saved" ? { ...prev, [id]: "idle" } : prev,
        );
      }, 2000);
    },
    onError: (_err, { id }) => {
      setRowStates((prev) => ({ ...prev, [id]: "error" }));
    },
  });

  const handleCategoryChange = (row: AdminChannelRow, category: string) => {
    if (category === row.category) return;
    patchMutation.mutate({
      id: row._id,
      payload: { category: category as AdminChannelRow["category"] },
    });
  };

  const handleTypeChange = (row: AdminChannelRow, type: string) => {
    if (type === row.type) return;
    patchMutation.mutate({
      id: row._id,
      payload: { type: type as AdminChannelRow["type"] },
    });
  };

  const channels = data?.channels ?? [];
  const total = data?.pagination.total ?? 0;

  const selectedChannelSummary = useMemo(() => {
    if (!selectedChannelId) return null;
    return channels.find((ch) => ch._id === selectedChannelId) ?? null;
  }, [selectedChannelId, channels]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={600}>
          Análisis de Canales
        </Typography>
        <Tooltip title="Actualizar">
          <span>
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            size="small"
            placeholder="Buscar por título o slug…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {CATEGORY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudieron cargar los canales.
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Canal</TableCell>
                <TableCell width={160}>Category</TableCell>
                <TableCell width={160}>Type</TableCell>
                <TableCell width={180}>Programa por defecto</TableCell>
                <TableCell>YouTube channel ID</TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : channels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No hay canales que coincidan con los filtros.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                channels.map((row) => {
                  const saveState = rowStates[row._id] ?? "idle";
                  const isRowBusy = saveState === "saving";
                  const imageUrl = getPodcastImageUrl(row);

                  return (
                    <TableRow
                      key={row._id}
                      hover
                      selected={selectedChannelId === row._id}
                      onClick={() => openChannelDrawer(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={imageUrl}
                            alt={row.title}
                            sx={{ width: 36, height: 36 }}
                          >
                            {row.title.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {row.title}
                            </Typography>
                            {row.slug && (
                              <Typography variant="caption" color="text.secondary">
                                {row.slug}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell onClick={stopRowClick}>
                        <Select
                          size="small"
                          fullWidth
                          value={row.category}
                          disabled={isRowBusy}
                          onChange={(e) =>
                            handleCategoryChange(row, e.target.value)
                          }
                        >
                          {CATEGORY_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell onClick={stopRowClick}>
                        <Select
                          size="small"
                          fullWidth
                          value={row.type}
                          disabled={isRowBusy}
                          onChange={(e) => handleTypeChange(row, e.target.value)}
                        >
                          {TYPE_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            row.defaultProgramName || row.defaultProgramId
                              ? "text.primary"
                              : "text.secondary"
                          }
                          noWrap
                          sx={{ maxWidth: 180 }}
                        >
                          {row.defaultProgramName ??
                            (row.defaultProgramId ? "—" : "Auto (nombre del canal)")}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontFamily="monospace"
                          color={
                            row.youtubeChannelId
                              ? "text.primary"
                              : "text.secondary"
                          }
                          noWrap
                          sx={{ maxWidth: 220 }}
                        >
                          {row.youtubeChannelId ?? "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {saveState === "saving" && (
                          <CircularProgress size={18} />
                        )}
                        {saveState === "saved" && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                        {saveState === "error" && (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="Filas"
        />
      </Paper>

      <ChannelDetailDrawer
        channelId={selectedChannelId}
        channelSummary={selectedChannelSummary}
        onClose={closeChannelDrawer}
      />
    </Container>
  );
};

export default ChannelAnalysisAdmin;
